/**
 * Service de notifications push navigateur
 * Gère l'abonnement et la désinscription aux notifications push
 */

import { pb } from './pocketbase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/**
 * Vérifie si les notifications push sont supportées par le navigateur
 * @returns {boolean}
 */
export const isPushSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Vérifie si l'utilisateur a accordé la permission pour les notifications
 * @returns {string} - 'granted', 'denied' ou 'default'
 */
export const getNotificationPermission = () => {
    if (!('Notification' in window)) {
        return 'denied';
    }
    return Notification.permission;
};

/**
 * Demande la permission pour les notifications
 * @returns {Promise<string>} - 'granted', 'denied' ou 'default'
 */
export const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
        console.warn('Les notifications ne sont pas supportées par ce navigateur');
        return 'denied';
    }

    try {
        const permission = await Notification.requestPermission();
        console.log(`📬 Permission de notification: ${permission}`);
        return permission;
    } catch (error) {
        console.error('Erreur demande de permission:', error);
        return 'denied';
    }
};

/**
 * Récupère la clé publique VAPID depuis le backend
 * @returns {Promise<string|null>}
 */
export const getVapidPublicKey = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/push/vapid-public-key`);
        
        if (!response.ok) {
            const error = await response.json();
            console.warn('Web Push non configuré:', error.message);
            return null;
        }

        const data = await response.json();
        return data.publicKey;
    } catch (error) {
        console.error('Erreur récupération clé VAPID:', error);
        return null;
    }
};

/**
 * Convertit une chaîne base64 URL-safe en Uint8Array
 * @param {string} base64String 
 * @returns {Uint8Array}
 */
const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

/**
 * Enregistre le Service Worker
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker non supporté');
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/'
        });
        console.log('✅ Service Worker enregistré:', registration.scope);
        return registration;
    } catch (error) {
        console.error('❌ Erreur enregistrement Service Worker:', error);
        return null;
    }
};

/**
 * S'abonne aux notifications push
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const subscribeToPushNotifications = async () => {
    // Vérifier le support
    if (!isPushSupported()) {
        return { 
            success: false, 
            message: 'Les notifications push ne sont pas supportées par ce navigateur' 
        };
    }

    // Vérifier l'authentification
    const token = pb.authStore.token;
    if (!token) {
        return { 
            success: false, 
            message: 'Utilisateur non authentifié' 
        };
    }

    try {
        // Demander la permission
        const permission = await requestNotificationPermission();
        if (permission !== 'granted') {
            return { 
                success: false, 
                message: 'Permission de notification refusée' 
            };
        }

        // Récupérer la clé VAPID
        const vapidPublicKey = await getVapidPublicKey();
        if (!vapidPublicKey) {
            return { 
                success: false, 
                message: 'Service de notifications push non configuré sur le serveur' 
            };
        }

        // Enregistrer le Service Worker
        const registration = await registerServiceWorker();
        if (!registration) {
            return { 
                success: false, 
                message: 'Impossible d\'enregistrer le Service Worker' 
            };
        }

        // Attendre que le Service Worker soit prêt
        await navigator.serviceWorker.ready;

        // S'abonner aux push
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });

        console.log('📬 Abonnement push créé:', subscription.endpoint);

        // Envoyer l'abonnement au backend
        const response = await fetch(`${API_BASE_URL}/api/push/subscribe`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ subscription: subscription.toJSON() })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erreur lors de l\'enregistrement');
        }

        console.log('✅ Abonnement push enregistré sur le serveur');
        return { 
            success: true, 
            message: 'Notifications push activées' 
        };

    } catch (error) {
        console.error('❌ Erreur abonnement push:', error);
        return { 
            success: false, 
            message: error.message || 'Erreur lors de l\'activation des notifications' 
        };
    }
};

/**
 * Se désabonne des notifications push
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const unsubscribeFromPushNotifications = async () => {
    try {
        const token = pb.authStore.token;
        if (!token) {
            return { success: false, message: 'Utilisateur non authentifié' };
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
            return { success: true, message: 'Aucun abonnement actif' };
        }

        // Désabonner localement
        await subscription.unsubscribe();

        // Informer le backend
        await fetch(`${API_BASE_URL}/api/push/unsubscribe`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ endpoint: subscription.endpoint })
        });

        console.log('✅ Désabonnement push réussi');
        return { success: true, message: 'Notifications push désactivées' };

    } catch (error) {
        console.error('❌ Erreur désabonnement push:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Vérifie si l'utilisateur est abonné aux notifications push
 * @returns {Promise<boolean>}
 */
export const isPushSubscribed = async () => {
    try {
        if (!isPushSupported()) {
            return false;
        }

        // Vérifier d'abord si un Service Worker est enregistré
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length === 0) {
            return false;
        }

        // Chercher un abonnement dans tous les Service Workers enregistrés
        for (const registration of registrations) {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription !== null) {
                return true;
            }
        }
        
        return false;
    } catch (error) {
        console.error('Erreur vérification abonnement:', error);
        return false;
    }
};

/**
 * Affiche une notification locale (test)
 * @param {string} title 
 * @param {string} message 
 * @param {string} link 
 */
export const showLocalNotification = async (title, message, link = '/') => {
    if (Notification.permission !== 'granted') {
        console.warn('Permission de notification non accordée');
        return;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        
        await registration.showNotification(title, {
            body: message,
            icon: '/images/logo-192.png',
            badge: '/images/badge-72.png',
            tag: 'local-notification',
            data: { url: link }
        });
    } catch (error) {
        console.error('Erreur affichage notification:', error);
    }
};
